import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { formatCurrency } from "@/lib/utils-date";
import KpiCard from "@/components/KpiCard";
import WelcomeBanner from "@/components/WelcomeBanner";
import Insights from "@/components/Insights";
import {
  AreaChart,
  Area,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  BarChart,
  Bar,
} from "recharts";
import {
  Package,
  AlertCircle,
  Clock,
  PackageX,
  TrendingUp,
  Euro,
  ShoppingBag,
} from "lucide-react";

export default function Dashboard() {
  const [summary, setSummary] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      const [s, a, al] = await Promise.all([
        api.dashboardSummary(),
        api.salesAnalytics(30),
        api.getAlerts(),
      ]);
      setSummary(s);
      setAnalytics(a);
      setAlerts(al);
    })();
  }, []);

  return (
    <div data-testid="dashboard-page" className="space-y-8">
      <WelcomeBanner onReset={() => window.location.reload()} />

      <Insights />

      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#8E918C] mb-2">
          Panoramica
        </p>
        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight" style={{ fontFamily: "Work Sans, sans-serif" }}>
          Buongiorno,{" "}
          <span className="text-[#2E4A3B]">il tuo magazzino</span>
        </h1>
        <p className="text-[#5C5F5A] mt-2">
          Tieni sotto controllo scadenze, stock e vendite in un colpo d&apos;occhio.
        </p>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <KpiCard
          testid="kpi-total-products"
          label="Prodotti in magazzino"
          value={summary?.total_products ?? "—"}
          icon={Package}
          tone="neutral"
          subtitle={(() => {
            const pieces = summary?.total_stock ?? 0;
            const kg = summary?.total_stock_kg ?? 0;
            const kgStr = kg > 0 ? `${kg.toFixed(3).replace(".", ",")} kg` : null;
            return kgStr ? `${pieces} pezzi · ${kgStr}` : `${pieces} pezzi`;
          })()}
          onClick={() => navigate("/app/prodotti")}
        />
        <KpiCard
          testid="kpi-expiring"
          label="In scadenza (7 giorni)"
          value={summary?.expiring_soon ?? "—"}
          icon={Clock}
          tone="warning"
          subtitle={summary?.expired ? `${summary.expired} già scaduti` : "Nessuno scaduto"}
          onClick={() => navigate("/app/prodotti?filter=expiring")}
        />
        <KpiCard
          testid="kpi-stagnant"
          label="Prodotti fermi"
          value={summary?.stagnant ?? "—"}
          icon={PackageX}
          tone="info"
          subtitle="Oltre 30 giorni senza vendite"
          onClick={() => navigate("/app/prodotti?filter=stagnant")}
        />
        <KpiCard
          testid="kpi-weekly-revenue"
          label="Incassi 7 giorni"
          value={summary ? formatCurrency(summary.weekly_revenue) : "—"}
          icon={Euro}
          tone="success"
          subtitle={`${summary?.weekly_sales_count ?? 0} vendite`}
          onClick={() => navigate("/app/vendite")}
        />
      </div>

      {/* Charts + Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mag-card p-6 lg:col-span-2"
        >
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold" style={{ fontFamily: "Work Sans, sans-serif" }}>
                Andamento vendite · 30 giorni
              </h3>
              <p className="text-sm text-[#8E918C] mt-0.5">
                Totale: {formatCurrency(analytics?.total_revenue || 0)} · {analytics?.total_units || 0} unità · <span className="italic">clicca un giorno</span>
              </p>
            </div>
            <TrendingUp size={20} className="text-[#2E4A3B]" />
          </div>
          <div className="h-64">
            {analytics ? (
              <ResponsiveContainer width="100%" height={256}>
                <AreaChart
                  data={analytics.daily || []}
                  onClick={(state) => {
                    const day = state?.activePayload?.[0]?.payload?.date;
                    if (day) navigate(`/app/vendite?date=${day}`);
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <defs>
                    <linearGradient id="fillRevenue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#2E4A3B" stopOpacity={0.25} />
                      <stop offset="100%" stopColor="#2E4A3B" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="#EAE7E1" vertical={false} />
                  <XAxis
                    dataKey="date"
                    stroke="#8E918C"
                    fontSize={11}
                    tickFormatter={(v) => {
                      const d = new Date(v);
                      return d.toLocaleDateString("it-IT", { day: "2-digit", month: "short" });
                    }}
                  />
                  <YAxis stroke="#8E918C" fontSize={11} tickFormatter={(v) => `€${v}`} />
                  <Tooltip
                    contentStyle={{
                      background: "#FFFFFF",
                      border: "1px solid #EAE7E1",
                      borderRadius: 8,
                      fontSize: 12,
                    }}
                    formatter={(v) => [formatCurrency(v), "Incasso"]}
                    labelFormatter={(v) => new Date(v).toLocaleDateString("it-IT")}
                  />
                  <Area
                    type="monotone"
                    dataKey="revenue"
                    stroke="#2E4A3B"
                    strokeWidth={2}
                    fill="url(#fillRevenue)"
                    activeDot={{
                      r: 6,
                      cursor: "pointer",
                      onClick: (_e, payload) => {
                        const day = payload?.payload?.date;
                        if (day) navigate(`/app/vendite?date=${day}`);
                      },
                    }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div data-testid="chart-skeleton-revenue" className="h-full w-full rounded-md bg-[#F4F1ED] animate-pulse" />
            )}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mag-card p-6"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold" style={{ fontFamily: "Work Sans, sans-serif" }}>
              Avvisi urgenti
            </h3>
            <AlertCircle size={20} className="text-[#C65D47]" />
          </div>
          <div className="space-y-3 max-h-64 overflow-y-auto pr-1">
            {alerts.length === 0 ? (
              <div className="text-sm text-[#8E918C] py-8 text-center">Tutto sotto controllo.</div>
            ) : (
              alerts.slice(0, 8).map((a) => (
                <AlertRow
                  key={a.id}
                  alert={a}
                  onClick={() => {
                    if (a.type === "low_stock") {
                      navigate("/app/fornitori#riordino");
                    } else if (a.product_id) {
                      navigate(`/app/prodotti?open=${a.product_id}`);
                    }
                  }}
                />
              ))
            )}
          </div>
        </motion.div>
      </div>

      {/* Top sellers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mag-card p-6"
        >
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold" style={{ fontFamily: "Work Sans, sans-serif" }}>
                Più venduti
              </h3>
              <p className="text-sm text-[#8E918C] mt-0.5">Top per unità vendute · <span className="italic">clicca una barra</span></p>
            </div>
            <ShoppingBag size={20} className="text-[#2E4A3B]" />
          </div>
          <div className="h-64">
            {analytics ? (
              <ResponsiveContainer width="100%" height={256}>
                <BarChart
                  data={(analytics.top_by_units || []).slice(0, 6)}
                  layout="vertical"
                  margin={{ left: 10 }}
                  onClick={(state) => {
                    const pid = state?.activePayload?.[0]?.payload?.product_id;
                    if (pid) navigate(`/app/prodotti?open=${pid}`);
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <CartesianGrid stroke="#EAE7E1" horizontal={false} />
                  <XAxis type="number" stroke="#8E918C" fontSize={11} />
                  <YAxis
                    dataKey="product_name"
                    type="category"
                    stroke="#5C5F5A"
                    fontSize={11}
                    width={140}
                    tickFormatter={(v) => (v.length > 22 ? v.slice(0, 22) + "…" : v)}
                  />
                  <Tooltip
                    contentStyle={{
                      background: "#FFFFFF",
                      border: "1px solid #EAE7E1",
                      borderRadius: 8,
                      fontSize: 12,
                    }}
                    formatter={(v) => [`${v} unità`, ""]}
                  />
                  <Bar
                    dataKey="units"
                    fill="#2E4A3B"
                    radius={[0, 4, 4, 0]}
                    cursor="pointer"
                    onClick={(data) => {
                      const pid = data?.product_id || data?.payload?.product_id;
                      if (pid) navigate(`/app/prodotti?open=${pid}`);
                    }}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div data-testid="chart-skeleton-top" className="h-full w-full rounded-md bg-[#F4F1ED] animate-pulse" />
            )}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mag-card p-6"
        >
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold" style={{ fontFamily: "Work Sans, sans-serif" }}>
                Prodotti fermi
              </h3>
              <p className="text-sm text-[#8E918C] mt-0.5">
                Senza vendite negli ultimi 30 giorni
              </p>
            </div>
            <PackageX size={20} className="text-[#4A7B9D]" />
          </div>
          <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
            {analytics?.stagnant_products?.length === 0 ? (
              <div className="text-sm text-[#8E918C] py-8 text-center">
                Tutti i prodotti stanno vendendo bene!
              </div>
            ) : (
              (analytics?.stagnant_products || []).slice(0, 10).map((p) => (
                <button
                  type="button"
                  key={p.product_id}
                  data-testid="stagnant-item"
                  onClick={() => navigate(`/app/prodotti?open=${p.product_id}`)}
                  className="w-full text-left flex items-center justify-between p-3 rounded-lg bg-[#F9F8F6] border border-[#EAE7E1] hover:bg-[#F4F1ED] hover:border-[#D8D3CB] transition-colors cursor-pointer"
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{p.product_name}</div>
                    <div className="text-xs text-[#8E918C]">{p.category}</div>
                  </div>
                  <span className="text-xs font-mono text-[#5C5F5A] bg-white border border-[#EAE7E1] px-2 py-1 rounded-md">
                    {p.stock} u.
                  </span>
                </button>
              ))
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
}

function AlertRow({ alert, onClick }) {
  const styles = {
    expired: { cls: "badge-expired", label: "Scaduto" },
    expiring: { cls: "badge-expiring", label: "In scadenza" },
    stagnant: { cls: "badge-stagnant", label: "Fermo" },
    low_stock: { cls: "badge-low", label: "Stock basso" },
  };
  const st = styles[alert.type] || styles.expiring;
  const isClickable = typeof onClick === "function";
  const Cmp = isClickable ? "button" : "div";
  return (
    <Cmp
      type={isClickable ? "button" : undefined}
      onClick={onClick}
      className={`w-full text-left flex items-start gap-3 p-3 rounded-lg border border-[#EAE7E1] ${
        isClickable ? "hover:bg-[#F4F1ED] hover:border-[#D8D3CB] transition-colors cursor-pointer" : ""
      }`}
    >
      <span className={`${st.cls} px-2 py-0.5 rounded-md text-[10px] font-semibold uppercase tracking-wide flex-shrink-0 mt-0.5`}>
        {st.label}
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium truncate">{alert.product_name}</div>
        <div className="text-xs text-[#8E918C] mt-0.5">{alert.message}</div>
      </div>
    </Cmp>
  );
}
